/**
 * Image Generator Types, Constants, and Helpers
 *
 * Shared types and utilities for the Image Generator arche.
 * Extracted from ImageGeneratorPage to enable reuse across hooks and components.
 */

import { ParameterDefinition } from '@/lib/api';
import { ImageGenerationStage } from '../components/ImageGenerationProgress';

// ============================================================================
// Constants
// ============================================================================

/** Number of AI instances supported */
export const NUM_INSTANCES = 3;

/** Maximum number of enhancement history items per category */
export const MAX_HISTORY_ITEMS = 10;

/** Arche ID for image-generator */
export const ARCHE_ID = 'arche_image_generator';

// localStorage key functions (scoped to userId)
export function getEnhancementHistoryStorageKey(userId: string): string {
  return `fias-image-enhancement-history-${userId}`;
}
export function getPresetsStorageKey(userId: string): string {
  return `fias-image-presets-${userId}`;
}
export function getInstanceConfigsStorageKey(userId: string): string {
  return `fias-image-instance-configs-${userId}`;
}
export function getTagDefinitionsStorageKey(userId: string): string {
  return `fias-image-tag-definitions-${userId}`;
}

// Fallback options when model definition is not yet loaded
export const FALLBACK_SIZE_OPTIONS = [
  { value: '1024x1024', label: 'Square (1024x1024)' },
  { value: '1792x1024', label: 'Landscape (1792x1024)' },
  { value: '1024x1792', label: 'Portrait (1024x1792)' },
];

export const FALLBACK_QUALITY_OPTIONS = [
  { value: 'standard', label: 'Standard' },
  { value: 'hd', label: 'HD' },
];

export const FALLBACK_STYLE_OPTIONS = [
  { value: 'vivid', label: 'Vivid' },
  { value: 'natural', label: 'Natural' },
];

/** Prompt enhancement categories */
export const ENHANCEMENT_CATEGORIES = [
  {
    key: 'subjectDetails',
    label: 'Subject Details',
    description:
      'Physical characteristics (size, shape, color, texture), specific features (fur type, facial expression, pose), age, condition, or state of the subject.',
    placeholder: 'e.g., fluffy orange fur, sitting pose, curious expression',
  },
  {
    key: 'composition',
    label: 'Composition & Framing',
    description:
      "Camera angle (close-up, wide shot, bird's eye view), subject positioning (centered, rule of thirds), depth of field, and focus points.",
    placeholder: 'e.g., close-up shot, centered, shallow depth of field',
  },
  {
    key: 'lighting',
    label: 'Lighting',
    description:
      'Light source type (natural, studio, dramatic), direction (backlit, side-lit, soft diffused), and time of day implications (golden hour, midday).',
    placeholder: 'e.g., soft natural lighting, golden hour, backlit',
  },
  {
    key: 'styleMedium',
    label: 'Style & Medium',
    description:
      'Artistic style (photorealistic, painterly, illustration), rendering technique (3D render, watercolor, digital art), and artistic influences or era references.',
    placeholder: 'e.g., photorealistic, oil painting style, Studio Ghibli',
  },
  {
    key: 'background',
    label: 'Background & Setting',
    description:
      'Environment description (indoor, outdoor, abstract), location context (forest, city, studio backdrop), and supporting elements and props.',
    placeholder: 'e.g., cozy living room, autumn forest, minimalist backdrop',
  },
  {
    key: 'mood',
    label: 'Mood & Atmosphere',
    description:
      'Emotional tone (serene, dramatic, whimsical) and ambiance (cozy, mysterious, energetic).',
    placeholder: 'e.g., peaceful, mysterious, joyful energy',
  },
  {
    key: 'colorTone',
    label: 'Color & Tone',
    description:
      'Color palette suggestions, contrast and saturation levels, and color temperature (warm, cool).',
    placeholder: 'e.g., warm earth tones, high contrast, muted pastels',
  },
  {
    key: 'technicalQuality',
    label: 'Technical Quality',
    description:
      'Resolution/detail references ("highly detailed", "4K") and rendering quality descriptors.',
    placeholder: 'e.g., highly detailed, 4K, sharp focus',
  },
] as const;

// ============================================================================
// Provider Parameters (Engine-Specific Settings)
// ============================================================================

/** Provider params are an open record of engine-specific values */
export type ProviderParams = Record<string, unknown>;

/** Empty provider params default */
export const EMPTY_PROVIDER_PARAMS: ProviderParams = {};

/** Control type for rendering a provider param field */
export type ProviderParamControlType = 'textarea' | 'slider' | 'number' | 'checkbox';

/** Definition for a single provider parameter */
export interface ProviderParamDefinition {
  key: string;
  label: string;
  description: string;
  controlType: ProviderParamControlType;
  /** Models that support this param (by model ID) */
  supportedModels: string[];
  /** Default value */
  defaultValue: string | number | boolean;
  /** For slider/number: minimum value */
  min?: number;
  /** For slider/number: maximum value */
  max?: number;
  /** For slider: step increment */
  step?: number;
}

/** All provider param definitions */
export const PROVIDER_PARAM_DEFINITIONS: ProviderParamDefinition[] = [
  {
    key: 'negative_prompt',
    label: 'Negative Prompt',
    description:
      "Describes what you don't want in the image. Supported by SD 3.5 Large and SD 3.5 Medium. Will not be applied if the selected model does not support it.",
    controlType: 'textarea',
    supportedModels: ['sd3.5-large', 'sd3.5-medium'],
    defaultValue: '',
  },
  {
    key: 'cfg_scale',
    label: 'CFG Scale',
    description:
      'Controls how closely the image follows the prompt. Higher values = more literal. Supported by SD 3.5 Large. Will not be applied if the selected model does not support it.',
    controlType: 'slider',
    supportedModels: ['sd3.5-large'],
    defaultValue: 7,
    min: 1,
    max: 15,
    step: 0.5,
  },
  {
    key: 'seed',
    label: 'Seed',
    description:
      'Fixed seed for reproducible results. Use the same seed + prompt to get identical images. Supported by all Stability, BFL, and Recraft models. Will not be applied if the selected model does not support it.',
    controlType: 'number',
    supportedModels: [
      // Stability
      'sd3.5-large',
      'sd3.5-large-turbo',
      'sd3.5-medium',
      'stable-image-ultra',
      'stable-image-core',
      // BFL
      'flux-2-pro',
      'flux-1.1-pro',
      'flux-1.1-pro-ultra',
      'flux-2-flex',
      'flux-2-klein-9b',
      // Recraft
      'recraft-v3',
      'recraftv4',
      'recraftv4_vector',
      'recraftv4_pro',
      'recraftv4_pro_vector',
    ],
    defaultValue: '',
    min: 0,
    max: 4294967295,
  },
  {
    key: 'guidance',
    label: 'Guidance',
    description:
      'Controls how strongly the model follows the prompt. Higher = more adherence. Supported by FLUX.2 Flex. Will not be applied if the selected model does not support it.',
    controlType: 'slider',
    supportedModels: ['flux-2-flex'],
    defaultValue: 3.5,
    min: 1.5,
    max: 10.0,
    step: 0.5,
  },
  {
    key: 'steps',
    label: 'Steps',
    description:
      'Number of inference steps. More steps = higher quality but slower. Supported by FLUX.2 Flex. Will not be applied if the selected model does not support it.',
    controlType: 'slider',
    supportedModels: ['flux-2-flex'],
    defaultValue: 28,
    min: 1,
    max: 50,
    step: 1,
  },
  {
    key: 'raw',
    label: 'Raw Mode',
    description:
      'Disables prompt enhancement for more literal results. Supported by FLUX 1.1 Pro Ultra. Will not be applied if the selected model does not support it.',
    controlType: 'checkbox',
    supportedModels: ['flux-1.1-pro-ultra'],
    defaultValue: false,
  },
];

/** Returns the param definitions applicable to a given model */
export function getProviderParamsForModel(modelName: string): ProviderParamDefinition[] {
  return PROVIDER_PARAM_DEFINITIONS.filter((def) => def.supportedModels.includes(modelName));
}

/** Returns true if the model has any engine-specific parameters */
export function modelHasProviderParams(modelName: string): boolean {
  return PROVIDER_PARAM_DEFINITIONS.some((def) => def.supportedModels.includes(modelName));
}

/** Filters a providerParams record to only include params supported by the given model */
export function filterProviderParamsForModel(
  providerParams: ProviderParams,
  modelName: string,
): ProviderParams {
  const applicableKeys = new Set(getProviderParamsForModel(modelName).map((def) => def.key));
  const filtered: ProviderParams = {};
  for (const [key, value] of Object.entries(providerParams)) {
    if (applicableKeys.has(key) && value !== '' && value !== undefined) {
      filtered[key] = value;
    }
  }
  return filtered;
}

// ============================================================================
// Types
// ============================================================================

export type EnhancementKey = (typeof ENHANCEMENT_CATEGORIES)[number]['key'];

/** Generated image data */
export interface GeneratedImage {
  requestId: string;
  imageRef: string;
  imageUrl: string;
  prompt: string;
  revisedPrompt?: string;
  provider: string;
  model: string;
  size: string;
  quality: string;
  style: string;
  enhancements: Record<EnhancementKey, string>;
  generatedAt: Date;
  instanceId?: number;
}

/** Image settings preset */
export interface ImagePreset {
  id: string;
  name: string;
  size: string;
  quality: string;
  style: string;
  enhancements: Record<EnhancementKey, string>;
  providerParams: ProviderParams;
  /** When set, the preset includes a specific model */
  modelName?: string;
  /** Provider for the saved model */
  provider?: string;
  createdAt: string;
  tags: string[];
  categoryId: string | null;
}

/** Tag definition for organizing presets */
export interface TagDefinition {
  id: string;
  name: string;
}

/** Per-instance configuration */
export interface ImageInstanceConfig {
  instanceId: number;
  presetName: string;
  originalPresetId?: string | null;
  autoSave: boolean;
  /** Whether to include the model when saving presets */
  includeModelInPreset: boolean;
  modelName: string;
  provider: string;
  size: string;
  quality: string;
  style: string;
  enhancements: Record<EnhancementKey, string>;
  providerParams: ProviderParams;
}

/** Per-instance state */
export interface ImageInstanceState {
  stage: ImageGenerationStage;
  progressMessage: string;
  currentRequestId: string | null;
  error: string | null;
}

// ============================================================================
// Factory Functions
// ============================================================================

/** Default empty enhancements */
export const EMPTY_ENHANCEMENTS = ENHANCEMENT_CATEGORIES.reduce(
  (acc, cat) => ({ ...acc, [cat.key]: '' }),
  {} as Record<EnhancementKey, string>,
);

/** Creates default instance config */
export function createDefaultInstanceConfig(instanceId: number): ImageInstanceConfig {
  return {
    instanceId,
    presetName: 'Default Preset',
    autoSave: true,
    includeModelInPreset: false,
    modelName: 'dall-e-3',
    provider: 'openai',
    size: '1024x1024',
    quality: 'standard',
    style: 'vivid',
    enhancements: { ...EMPTY_ENHANCEMENTS },
    providerParams: {},
  };
}

/** Creates default instance state */
export function createDefaultInstanceState(): ImageInstanceState {
  return {
    stage: 'idle',
    progressMessage: '',
    currentRequestId: null,
    error: null,
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

/** Suffix appended to a preset name when the user modifies settings */
const UNSAVED_SUFFIX = ' (Unsaved)';

/** Returns true if the preset name indicates unsaved modifications */
export function isUnsavedPresetName(name: string): boolean {
  return name.endsWith(UNSAVED_SUFFIX);
}

/** Appends "(Unsaved)" to a preset name if not already present */
export function toUnsavedPresetName(name: string): string {
  return isUnsavedPresetName(name) ? name : name + UNSAVED_SUFFIX;
}

/** Strips the "(Unsaved)" suffix to recover the original preset name */
export function toBasePresetName(name: string): string {
  return isUnsavedPresetName(name) ? name.slice(0, -UNSAVED_SUFFIX.length) : name;
}

/** Extracts options from a parameter schema for a specific parameter name */
export function getOptionsFromSchema(
  parameterSchema: ParameterDefinition[],
  paramName: string,
  fallback: { value: string; label: string }[],
): { value: string; label: string }[] {
  const param = parameterSchema.find((p) => p.name === paramName);
  if (param?.options && param.options.length > 0) {
    return param.options.map((opt) => ({
      value: opt.value,
      label: opt.label,
    }));
  }
  return fallback;
}

/** Format model name for display (e.g., 'dall-e-3' -> 'DALL-E 3') */
export function formatModelName(modelName: string): string {
  const displayNames: Record<string, string> = {
    // OpenAI
    'dall-e-3': 'DALL-E 3',
    'dall-e-2': 'DALL-E 2',
    'gpt-image-1.5': 'GPT Image 1.5',
    // Google
    'imagen-3.0-generate-001': 'Imagen 3',
    'imagen-4.0-fast-generate-001': 'Imagen 4',
    'gemini-2.5-flash-image': 'Nano Banana',
    // BFL / FLUX
    'flux-2-pro': 'FLUX.2 Pro',
    'flux-1.1-pro': 'FLUX 1.1 Pro',
    'flux-1.1-pro-ultra': 'FLUX 1.1 Pro Ultra',
    'flux-2-flex': 'FLUX.2 Flex',
    'flux-2-klein-9b': 'FLUX.2 Klein 9B',
    // Recraft
    'recraft-v3': 'Recraft V3',
    recraftv4: 'Recraft V4',
    recraftv4_vector: 'Recraft V4 Vector',
    recraftv4_pro: 'Recraft V4 Pro',
    recraftv4_pro_vector: 'Recraft V4 Pro Vector',
    // Stability AI
    'sd3.5-large': 'SD 3.5 Large',
    'sd3.5-large-turbo': 'SD 3.5 Large Turbo',
    'sd3.5-medium': 'SD 3.5 Medium',
    'stable-image-ultra': 'Stable Image Ultra',
    'stable-image-core': 'Stable Image Core',
  };
  return displayNames[modelName] || modelName;
}

/** Get aspect ratio class based on image size string */
export function getAspectRatioClass(size: string): string {
  // Handle aspect ratio strings
  if (size === '16:9') return 'aspect-video';
  if (size === '9:16') return 'aspect-[9/16]';
  if (size === '4:3') return 'aspect-[4/3]';
  if (size === '3:4') return 'aspect-[3/4]';
  if (size === '3:2') return 'aspect-[3/2]';
  if (size === '2:3') return 'aspect-[2/3]';
  if (size === '5:4') return 'aspect-[5/4]';
  if (size === '4:5') return 'aspect-[4/5]';
  if (size === '21:9') return 'aspect-[21/9]';
  if (size === '9:21') return 'aspect-[9/21]';

  // Handle pixel dimensions (WxH)
  const match = size.match(/^(\d+)x(\d+)$/);
  if (match) {
    const w = parseInt(match[1], 10);
    const h = parseInt(match[2], 10);
    if (w === h) return 'aspect-square';
    return `aspect-[${w}/${h}]`;
  }

  return 'aspect-square';
}

/**
 * Returns the estimated generation duration in milliseconds for a given model.
 * Used to calibrate the progress bar animation.
 */
export function getEstimatedDurationMs(modelName: string): number {
  switch (modelName) {
    // OpenAI
    case 'dall-e-3':
      return 18000;
    case 'gpt-image-1.5':
      return 25000;
    // Google
    case 'imagen-4.0-fast-generate-001':
      return 25000;
    case 'gemini-2.5-flash-image':
      return 15000;
    // BFL / FLUX
    case 'flux-2-pro':
      return 30000;
    case 'flux-1.1-pro':
      return 25000;
    case 'flux-1.1-pro-ultra':
      return 35000;
    case 'flux-2-flex':
      return 20000;
    case 'flux-2-klein-9b':
      return 8000;
    // Recraft
    case 'recraft-v3':
      return 20000;
    case 'recraftv4':
      return 15000;
    case 'recraftv4_vector':
      return 15000;
    case 'recraftv4_pro':
      return 25000;
    case 'recraftv4_pro_vector':
      return 25000;
    // Stability AI
    case 'sd3.5-large':
      return 20000;
    case 'sd3.5-large-turbo':
      return 8000;
    case 'sd3.5-medium':
      return 15000;
    case 'stable-image-ultra':
      return 30000;
    case 'stable-image-core':
      return 10000;
    default:
      return 18000;
  }
}
